import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ClinicContentBlock } from '../../../data/models/ClinicContentBlock';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { CONSTANTS } from '../../../helper/constants';
import { environment } from '../../../../environments/environment';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-image-block-dialog',
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule],
  templateUrl: './image-block-dialog.component.html',
  styleUrl: './image-block-dialog.component.scss'
})
export class ImageBlockDialogComponent implements OnInit, OnDestroy {
  form!: FormGroup
  isEdit: boolean
  selectedFile: File | null = null
  imagePreviewUrl: string | null = null
  fileError: string | null = null
  readonly maxInput = CONSTANTS.maxInput
  private blobUrl: string | null = null
  private readonly maxFileSize = 3 * 1024 * 1024
  private hasFileChange = false
  private isClosing = false

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ImageBlockDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { block?: ClinicContentBlock }
  ) {
    this.isEdit = !!data?.block
  }

  ngOnInit(): void {
    this.dialogRef.backdropClick().subscribe(() => this.tryClose(null))
    this.dialogRef.keydownEvents().subscribe(e => {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.tryClose(null)
      }
    })

    this.form = this.fb.group({
      title: [this.data?.block?.title || '', [Validators.maxLength(CONSTANTS.maxInput)]],
      body_text: [this.data?.block?.body_text || '', [Validators.maxLength(CONSTANTS.maxInput)]]
    })

    if (this.isEdit && this.data.block?.image_path) {
      const path = this.data.block.image_path
      if (path.startsWith('blob:')) {
        this.imagePreviewUrl = path
      } else {
        this.imagePreviewUrl = `${environment.url}/${environment.backendVersion}/clinic/media/${path}`
      }
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.files?.length) return

    const file = input.files[0]
    this.fileError = null

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      this.fileError = 'Nur JPG und PNG Dateien sind erlaubt.'
      return
    }

    if (file.size > this.maxFileSize) {
      this.fileError = CONSTANTS.fileSizeExceededError
      return
    }

    this.selectedFile = file
    this.hasFileChange = true
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
    this.blobUrl = URL.createObjectURL(file)
    this.imagePreviewUrl = this.blobUrl
  }

  ngOnDestroy(): void {
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
  }

  get isDirty(): boolean {
    return this.form.dirty || this.hasFileChange
  }

  get isValid(): boolean {
    if (this.form.invalid) return false
    if (!this.isEdit && !this.selectedFile) return false
    return true
  }

  get saveBlockedReason(): string | null {
    const reasons: string[] = []

    if (this.form.controls['title'].hasError('maxlength')) {
      reasons.push(`Titel darf maximal ${this.maxInput} Zeichen lang sein.`)
    }
    if (this.form.controls['body_text'].hasError('maxlength')) {
      reasons.push(`Beschreibungstext darf maximal ${this.maxInput} Zeichen lang sein.`)
    }
    if (!this.isEdit && !this.selectedFile) {
      reasons.push('Bitte lade eine Bilddatei hoch.')
    }
    if (this.fileError) {
      reasons.push(this.fileError)
    }

    return reasons.length ? reasons.join(' ') : null
  }

  async tryClose(result: string | null): Promise<void> {
    if (this.isClosing) return
    if (this.isDirty) {
      this.isClosing = true
      try {
        const confirmed = await this.confirmDiscard()
        if (!confirmed) return
      } finally {
        this.isClosing = false
      }
    }
    this.dialogRef.close(result)
  }

  private async confirmDiscard(): Promise<boolean> {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Änderungen verwerfen',
        message: 'Du hast ungespeicherte Änderungen. Wenn du den Dialog verlässt, gehen diese verloren.',
        confirmText: 'Verwerfen'
      }
    })
    return !!(await firstValueFrom(ref.afterClosed()))
  }

  save(): void {
    if (!this.isValid) return
    this.dialogRef.close({
      ...this.form.value,
      file: this.selectedFile
    })
  }
}
